import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { guardLeave } from '../../utils/unsaved';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import type { AwDocActions, AwDocKind, AwExpenseDetail, AwNoteDetail, AwPayableDetail } from '../../types';
import DlBtn from '../DlBtn';
import { Dialog, Loader, emptyLine } from './dialog';
import { DocDetail } from './panes/Documents';
// Batch 12 tail T1 (FS-6): a supplier note's facts, the note window's own body (panes/Purchases.tsx NoteFacts) — the same deliberate
// cycle as the document window's (Purchases.tsx imports this file's useGoToExpenseRecord): every cross-reference is inside a body.
import { NoteFacts } from './panes/Purchases';
import { DocEntriesBtn } from './docEntries';
import { RecordEvidence } from './attachments';   // owner 2026-11-02: every record's supporting files and linked records, on these read-only windows too
import { amt, btn, card, dmy, errBox, note, num, openBlob, pillAmber, pillGreen, pillGrey, pillRed, table, td, th } from './ui';
import { fmtDateTime } from '../../utils/region';   // batch 11 R5: when a reversed payment was reversed

/**
 * COA-D1 (second half) · COA-D10 · COA-B-8 (second half) — FROM A VOUCHER TO THE PAPER BEHIND IT,
 * on screen, in the system (owner 2026-10-20).
 *
 * The gap this closes, stated as it was found: the voucher window shipped in batch 5
 * (JournalBook.tsx EntryDetailDialog) has a SOURCE DOCUMENT column — the kind, the number, and an
 * `openable` flag the server had already resolved — and the whole file contained ZERO onClick that
 * opened it. The column told an accountant which invoice produced the voucher and then left them to
 * go and find it by hand. 没界面=没做, in its usual disguise: a server answer with no way to act on it.
 *
 * ⛔ THE SCREEN, NEVER A DOWNLOAD. "Open the document" here opens the DOCUMENT WINDOW — the same
 * window Sales › Invoices opens on a row click (panes/Documents.tsx DocDetail), mounted rather than
 * copied. It does not fetch a PDF. The customer's complaint that produced batch 4 was precisely
 * that clicking a thing in the books dropped a PDF into the Downloads folder, and repeating it one
 * level deeper would be the same defect with a different label. The PDF stays where it belongs: one
 * more click, inside the document window, exactly as it is from the list.
 *
 * ── ⛔ ONE PLACE THAT KNOWS WHICH SCREEN SHOWS WHICH KIND ────────────────────────────────────────
 * services/journalLinks.ts answers for ELEVEN document kinds (its DOC_KINDS set) and this file is
 * the only place in the client that maps one to a screen. Three answers, and they are different
 * sentences — never one grey button for all three:
 *
 *   1. THERE IS A SCREEN → a button that opens it (seven invoice-table doc_types + receipt →
 *      DocDetail, and since 2026-11-02 a quotation, which no voucher names but a related document
 *      does; `payable` → the bill; `expense_upload` → the scan).
 *   2. THE SERVER SAYS IT CANNOT BE OPENED (`openable: false`) → the server's OWN `why`, in words,
 *      and NO control. The resolver distinguishes "the field is empty", "it points at a voided
 *      document", "the VAT line was withdrawn" and "this was typed straight into the VAT ledger",
 *      and each of those sentences is the answer somebody needs when they are trying to find out
 *      what happened to a posted entry. Replacing them with a disabled button, or with a link that
 *      404s, throws away the only information there is.
 *   3. THE SERVER SAYS OPENABLE AND THIS PRODUCT HAS NO SCREEN → said in words, with no control.
 *      ⛔ `bank_line` IS this case, and it is not a bug in the resolver: a bank statement line is
 *      openable in the sense that the row exists and can be read (sourceOfEntry returns its date and
 *      description as the "document number"), but Banking › Transactions is a LIST with a match /
 *      ignore dialog and there is no per-line document window anywhere in the product. A link to the
 *      unfiltered list would not land on the line, so none is offered. Reported, not bolted on.
 *
 * ⛔ NEVER DERIVED FROM A NUMBER OR A NAME. `DOC_FAMILY` below maps the resolver's own stored
 * doc_type to the family key /acct-workspace/docs/:kind accepts. A document's type is not inferred
 * from the shape of its number, and a kind this file does not know is answer 3 — never a guess.
 *
 * ── ⛔ THE JUMP STACKS. IT NEVER CLOSES ANYTHING ─────────────────────────────────────────────────
 * Opening the document mounts a Dialog OVER the window the reader came from; nothing is unmounted,
 * so nothing typed anywhere behind it can be lost, and Esc answers the inner-most window by nesting
 * depth (utils/escLayer.ts). That is why no call below touches a host window's `onClose`: the
 * shell's dirty check (dialog.tsx useCloseGate) is reached through the shell on every exit, and a
 * jump that closed its host by calling the raw prop would be the one way round it. A confirm on a
 * jump that discards nothing would also be the false positive this repo refuses to teach.
 *
 * ── Why these two windows are not the register's own screens ─────────────────────────────────────
 * A supplier bill has a screen in Purchases › Payments (PayableDrawer) and a scan has a row in
 * Purchases › Supplier bills, and NEITHER is re-used here, on purpose. The drawer carries five
 * WRITE exits — record a payment, add a note, edit, book to VAT, unbook — each opening a
 * dirty-checked form. Opening it from the general ledger would put a read-only ledger path one
 * click from writing the books and from the filed-month gate, which this round must not do. So the
 * two windows below are read-only views of the SAME routes those screens read
 * (/payables/:id, /expenses/:id), they derive nothing, and each says in words where the bill or the
 * scan is actually worked on.
 *
 * ⛔ The import cycle is deliberate and is the house's: this file opens the document window, the
 * document window offers "what did this post" (docEntries.tsx), which opens the voucher window,
 * which offers this. Mutual navigation between a record and its link cannot be acyclic —
 * DocNoLink.tsx ↔ Peak/ReceiptForm.tsx is the same shape and has shipped for months. It is safe
 * because every cross-reference is inside a component body: nothing here is read while a module is
 * still evaluating.
 */

/** What the server resolved about one entry's source — services/journalLinks.ts SourceRef, as the
 *  journal routes return it. Kept structural rather than imported, so this file does not have to
 *  import the voucher screen in order to be imported BY it. */
export type SourceRef = { kind: string; id: string; docNo: string; openable: boolean; why: string;
  /** The Expenses register key this document is shown under (services/journalLinks.ts), when it has
   *  one. A bill and the scan it was made from share ONE row over there, so this is the key of the
   *  row — not of whichever half the entry happened to resolve to. */
  record?: { key: string; expenseNo: string };
  /** A voided document, reached from another's related documents (relatedDocs.tsx): its window reads it as one. No voucher says this. */
  voided?: boolean };

/**
 * ⛔ THE CLIENT IS THE VOUCHER'S OWN, not the shell's (owner 2026-10-20). Everything below is handed
 * a bare `clientId`, and the voucher window passes `d.clientId` — the client
 * GET /journal/entries/:entryId says that entry belongs to. Two reasons, and the second is the one
 * that decided it:
 *   · the document is read under the client the SERVER attributed the posting to, so a document can
 *     never be read under a client the voucher is not actually in; and
 *   · it needs NO new prop on EntryDetailDialog, so the three places that already open a voucher —
 *     the journal list, the interactive ledger's level 2, and the "what did this document post"
 *     panel — all gain the jump without one of them being left behind with a prop nobody passed.
 *     An optional ctx would have been exactly that: a silently missing feature at one entry point,
 *     which is 没界面=没做 wearing a prop's clothing.
 */

/**
 * The resolver's stored doc_type → the family key GET /acct-workspace/docs/:kind answers for.
 *
 * ⛔ The workspace splits the invoices table one step further than the portal does (Sales › Invoices
 * = invoice + proforma, Sales › Tax Invoices = the tax series), which is why this map is not the
 * identity. It mirrors FAMILY in routes/acctWorkspace.ts and nothing else; a doc_type missing from
 * here has no window and says so. Both notes stay on `note` after the owner's 2026-11-02 split into two
 * pages (`credit_note`, `debit_note`): it answers for either, and this window needs no more than that.
 */
const DOC_FAMILY: Record<string, AwDocKind> = {
  // A quotation posts nothing, so no voucher ever names one; a document's related documents do (relatedDocs.tsx, owner 2026-11-02).
  quotation: 'quotation',
  invoice: 'invoice', proforma: 'invoice',
  tax_invoice: 'tax_invoice', tax_invoice_receipt: 'tax_invoice',
  credit_note: 'note', debit_note: 'note',
  receipt: 'receipt',
};

/** Which window shows a kind. '' = this product has none (answer 3 in the header). `payroll` is not a
 *  window over this one but a place: the run lives in Payroll › Payroll List (batch 10 R7, PayrollRunOpen) — and
 *  so does a payroll payment's voucher, whose source the resolver answers as its run. `payroll_remit` is the
 *  same kind of place: a PND.1 or SSO remittance lives in Payroll › Payroll List › Remittances (batch 10 R8,
 *  PayrollRemitOpen). */
type SourceScreen = 'doc' | 'payable' | 'scan' | 'note' | 'payroll' | 'payroll_remit' | '';
// (Batch 12 tail T1, FS-6) `purchase_note` → 'note': a supplier's credit or debit note has its own window now (NoteDocDialog
// below, read-only), where until then it was named in NO_SCREEN_KEY as "a section of its bill".
const screenOf = (kind: string): SourceScreen =>
  DOC_FAMILY[kind] ? 'doc' : kind === 'payable' ? 'payable' : kind === 'expense_upload' ? 'scan' : kind === 'purchase_note' ? 'note' : kind === 'payroll_run' ? 'payroll'
    : kind === 'payroll_remittance' ? 'payroll_remit' : '';

/** Does a kind open in one of the windows below? A payroll run or remittance is a place to go, never a window to stack. */
export const opensAWindow = (kind: string): boolean => {
  const screen = screenOf(kind);
  return screen === 'doc' || screen === 'payable' || screen === 'scan' || screen === 'note';
};

/**
 * Does the row a window is about to read still stand? (Owner 2026-11-02) These windows now open from a related-documents panel that may
 * stand pinned and from a list that may be minutes old, and their reads answer a row gone since with a 404 and a JSON body, which pages the
 * admins: GET /doors always answers 200, so a gone row is a sentence in the window and no refused request. A verdict that cannot be read
 * does not stand in the way of the read (DocDetail's rule).
 */
export const stillStands = (clientId: string, kind: Parameters<typeof api.awRowDoors>[1], id: string): Promise<boolean> =>
  api.awRowDoors(clientId, kind, id).then((v: { found?: boolean }) => !!v.found, () => true);

const statusPill = (s: string): React.CSSProperties =>
  /paid|accepted|approved|settled/i.test(s) ? pillGreen : /void|reject|cancel/i.test(s) ? pillRed
    : /pending|partial|open/i.test(s) ? pillAmber : pillGrey;

/**
 * ⛔ A STORED VALUE PRINTS ITSELF WHEN THERE IS NO WORD FOR IT, and the fallback has to be decided
 * BEFORE t() is asked: i18next 23 answers an unknown key with the key, so the `t(k) || value` the
 * neighbouring registers are written with can only ever print `aw_pay_status_something` on screen.
 * JournalBook.tsx StatusTag learned this the hard way — it used to label a VOID voucher "Posted" —
 * so these maps name every value the tables store, each key written out in full so a grep finds it,
 * and anything unfamiliar is shown exactly as the database holds it.
 */
const PAY_STATUS_KEY: Record<string, string> = {
  open: 'aw_pay_status_open', partial: 'aw_pay_status_partial', paid: 'aw_pay_status_paid', void: 'aw_pay_status_void',
};
const PAY_METHOD_KEY: Record<string, string> = {
  transfer: 'aw_pay_method_transfer', cash: 'aw_pay_method_cash', cheque: 'aw_pay_method_cheque', other: 'aw_pay_method_other',
};
const SCAN_STATUS_KEY: Record<string, string> = {
  pending: 'aw_exp_pending', approved: 'aw_exp_approved', rejected: 'aw_exp_rejected',
};

/** One labelled fact, the voucher window's own shape (JournalBook.tsx Fact) so the two windows read alike. */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ minWidth: 150, flex: '0 1 auto' }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 2 }}>{children}</div>
  </div>
);

/**
 * The supplier bill, read-only (the `payable` kind).
 *
 * ⛔ NOT A SECOND SET OF FIGURES. Every number is `payableOut`'s own — the same route the payables
 * register reads — and the outstanding figure is the server's `outstanding`, never
 * to_pay − paid_total computed here: the server's version also accounts for the credit and debit
 * notes against the bill, and a subtraction on the glass would quietly disagree with the register.
 */
function PayableDocDialog({ clientId, id, docNo, onClose, record }: {
  clientId: string; id: string; docNo: string; onClose: () => void;
  /** The register row this document is shown under, when it has one — what "Open in Expenses"
   *  needs. A bill made from a scan and that scan share ONE row, so the key may not be this id's. */
  record?: { key: string; expenseNo: string };
}) {
  const { t } = useTranslation();
  const goToRecord = useGoToExpenseRecord();
  const [d, setD] = useState<AwPayableDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setD(null); setErr('');
    stillStands(clientId, 'payable', id)
      .then(there => {
        if (!alive) return null;
        if (!there) { setErr(t('aw_doc_menu_gone')); return null; }
        return api.awPayable(clientId, id).then((x: AwPayableDetail) => { if (alive) setD(x); });
      })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [clientId, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = d?.payable;
  return (
    <Dialog fullscreen title={t('aw_sd_payable_title')} docNo={docNo} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div style={errBox}>{err}</div>}
        {!d && !err && <Loader />}
        {p && (
          <>
            <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              <Fact label={t('aw_pay_bill')}><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{p.bill_no || '—'}</span></Fact>
              <Fact label={t('aw_exp_vendor')}>{p.vendor_name || '—'}{p.vendor_tax_id ? <span style={{ color: 'var(--text-tertiary)' }}> · {p.vendor_tax_id}</span> : ''}</Fact>
              <Fact label={t('aw_pay_bill_date')}>{p.bill_date ? dmy(p.bill_date) : '—'}</Fact>
              <Fact label={t('aw_pay_due_date')}>{p.due_date ? dmy(p.due_date) : '—'}</Fact>
              <Fact label={t('aw_col_status')}>
                <span style={statusPill(String(p.status))}>
                  {PAY_STATUS_KEY[p.status] ? t(PAY_STATUS_KEY[p.status]) : (String(p.status) || '—')}
                </span>
              </Fact>
              <Fact label={t('aw_col_currency')}>{p.currency || '—'}</Fact>
              {!!p.po_no && <Fact label={t('aw_po_no')}>{p.po_no}</Fact>}
              {!!p.expense_filename && <Fact label={t('aw_pay_linked_upload')}>{p.expense_filename}</Fact>}
            </div>
            <div style={{ ...card }}>
              <table style={table}>
                <tbody>
                  {([
                    ['aw_pay_total', p.total_amount], ['aw_pay_vat', p.vat_amount], ['aw_pay_wht', p.wht_amount],
                    ['aw_pay_to_pay', p.to_pay], ['aw_col_paid', p.paid_total],
                    ['aw_note_credit_total', p.credit_total], ['aw_note_debit_total', p.debit_total],
                  ] as [string, number][]).map(([k, v]) => (
                    <tr key={k}><td style={td}>{t(k)}</td><td style={num}>{amt(v)}</td></tr>
                  ))}
                  <tr>
                    <td style={{ ...td, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{t('aw_col_outstanding')}</td>
                    <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{amt(p.outstanding)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!!p.note && <div style={{ ...card, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{p.note}</div>}
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!d.payments.length ? <div style={emptyLine}>{t('aw_pay_no_payments')}</div> : (
                <table style={table}>
                  <thead><tr>
                    <th style={th}>{t('aw_col_date')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                    <th style={th}>{t('aw_pay_method')}</th>
                    <th style={th}>{t('aw_pay_reference')}</th>
                  </tr></thead>
                  <tbody>
                    {/* Batch 11 R5 (W2-22): the bill's history lists reversed payments too — struck here, with their reason,
                        as the register's own payments section shows them; never read as money still paid. */}
                    {d.payments.map(x => {
                      const gone: React.CSSProperties = x.deleted ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : {};
                      return (
                        <tr key={x.id} data-sd-payment-gone={x.deleted ? '1' : undefined}>
                          <td style={{ ...td, whiteSpace: 'nowrap', ...gone }}>{dmy(x.paid_date)}{x.payNo ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{x.payNo}</div> : null}</td>
                          <td style={{ ...num, ...gone }}>{amt(x.amount)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', ...gone }}>
                            {PAY_METHOD_KEY[x.method] ? t(PAY_METHOD_KEY[x.method]) : (String(x.method) || '—')}
                          </td>
                          <td style={td}><span style={gone}>{x.reference}</span>
                            {x.deleted && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                              {x.reverseReason ? t('aw_epay_reversed_by', { by: x.deletedByName || '—', when: x.deletedAt ? fmtDateTime(x.deletedAt) : '—', reason: x.reverseReason })
                                : t('aw_epay_taken_off_by', { by: x.deletedByName || '—', when: x.deletedAt ? fmtDateTime(x.deletedAt) : '—' })}
                            </div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <RecordEvidence clientId={clientId} kind="payable" id={id} />
            {/* ⛔ Where the bill is actually worked on, in words rather than as a second door: this
                window is read-only and the register's own screen is the one with the gates on it. */}
            <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <i className="ti ti-lock" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
              <div style={note}>{t('aw_sd_payable_readonly')}</div>
            </div>
            {/* ⛔ A LINK, not a second door. This window stays read-only; the register's own screen
                is the one carrying the gates, so the way to act on this document is to go there. */}
            {!!record?.key && (
              <button onClick={() => goToRecord(record.key, onClose)} style={openInExp}>
                <i className="ti ti-external-link" aria-hidden /> {t('aw_sd_open_in_expenses', { no: record.expenseNo || docNo })}
              </button>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * A supplier's credit or debit note, read-only (the `purchase_note` kind — batch 12 tail T1, FS-6): what a voucher's or an
 * entries panel's "via CN-…" opens. Its body is the note window's own (panes/Purchases.tsx NoteFacts — one set of facts,
 * read from GET /purchase-notes/:nid, never recomputed here), with Entries (a read) and "Open in Expenses".
 * ⛔ NO WRITE CONTROL, for PayableDocDialog's reason: the register's own window (Purchases › Debit & credit notes, the
 * bill's Notes) carries Edit, Book note, Unbook and Delete with their pre-checks, and a ledger path one click from them
 * would bypass none of those but would put writing the books beside reading them. "Open in Expenses" is a LINK there.
 */
function NoteDocDialog({ clientId, id, docNo, onClose }: { clientId: string; id: string; docNo: string; onClose: () => void }) {
  const { t } = useTranslation();
  const goToRecord = useGoToExpenseRecord();
  const [d, setD] = useState<AwNoteDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setD(null); setErr('');
    stillStands(clientId, 'purchase_note', id)
      .then(there => {
        if (!alive) return null;
        if (!there) { setErr(t('aw_doc_menu_gone')); return null; }
        return api.awPurchaseNote(clientId, id).then((x: AwNoteDetail) => { if (alive) setD(x); });
      })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [clientId, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const record = d?.record || null;
  return (
    <Dialog fullscreen title={d ? t(`aw_note_view_title_${d.note.kind}`) : t('aw_note_title')} docNo={docNo} onClose={onClose}>
      <div data-sd-note={id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div style={errBox}>{err}</div>}
        {!d && !err && <Loader />}
        {d && (
          <>
            <NoteFacts d={d} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <DocEntriesBtn ctx={{ clientId }} kind="purchase_note" id={d.note.id} docNo={d.note.note_no} />
              {/* ⛔ A LINK, not a second door (PayableDocDialog's rule). */}
              {!!record?.key && (
                <button onClick={() => goToRecord(record.key, onClose)} style={openInExp}>
                  <i className="ti ti-external-link" aria-hidden /> {t('aw_sd_open_in_expenses', { no: record.expenseNo || d.note.bill_no })}
                </button>
              )}
            </div>
            <RecordEvidence clientId={clientId} kind="purchase_note" id={d.note.id} />
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * The scan's sheets, ON SCREEN — lifted out of ScanDocDialog below unchanged (batch 7 R3a) so the expense
 * record window (panes/ExpenseRecords.tsx) shows the paper the same way: an <img> for a photographed
 * bill, an <object> for a PDF, a picker for a bill photographed over several sheets. ⛔ It never opens a
 * tab by itself; the buttons beside the sheet are the HOST's (`tools`): this file's "open the file
 * itself", the record window's explicit Download.
 */
export function ScanSheetView({ clientId, id, mime, filename, pageCount, tools }: {
  clientId: string; id: string; mime: string; filename: string; pageCount: number;
  tools?: (sheet: number) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState(1);
  const [url, setUrl] = useState('');
  const [fileErr, setFileErr] = useState('');
  // The sheet itself, as a blob URL. Revoked when this window (or the chosen sheet) goes away: an
  // object URL that is never released holds the whole file in memory for the life of the tab.
  useEffect(() => {
    let alive = true;
    let mine = '';
    setUrl(''); setFileErr('');
    api.awExpenseFileBlobUrl(clientId, id, sheet > 1 ? sheet : undefined)
      .then((u: string) => { if (alive) { mine = u; setUrl(u); } else URL.revokeObjectURL(u); })
      .catch((e: any) => { if (alive) setFileErr(String(e?.message || e)); });
    return () => { alive = false; if (mine) URL.revokeObjectURL(mine); };
  }, [clientId, id, sheet]);
  const isPdf = /pdf/i.test(String(mime || ''));
  return (
    <>
      {/* ⛔ The paper, on screen. A scan photographed over several sheets is picked here rather
          than silently showing the first one as though it were the whole bill. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
          {t('aw_sd_scan_sheet')}
        </span>
        {pageCount > 1 && Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
          <button key={n} onClick={() => setSheet(n)} aria-pressed={n === sheet}
            style={{ ...btn, padding: '4px 10px', fontSize: 12, ...(n === sheet ? { borderColor: 'var(--accent)', color: 'var(--accent-text)' } : {}) }}>
            {n}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {tools ? tools(sheet) : null}
      </div>
      {fileErr && <div style={errBox}>{fileErr}</div>}
      <div style={{ ...card, padding: 0, minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
        {!url && !fileErr ? <Loader /> : url ? (
          isPdf
            ? <object data={url} type="application/pdf" aria-label={filename} style={{ width: '100%', height: '68vh', border: 'none' }}>
                <div style={emptyLine}>{t('aw_sd_scan_no_inline')}</div>
              </object>
            : <img src={url} alt={filename} style={{ maxWidth: '100%', maxHeight: '68vh', display: 'block' }} />
        ) : null}
      </div>
    </>
  );
}

/**
 * The uploaded scan, read-only (the `expense_upload` kind).
 *
 * ⛔ THE SHEET IS SHOWN HERE, not handed to the browser. `awExpenseFileBlobUrl` fetches it with the
 * login token and gives back an object URL; this window renders that URL IN the window — an <img>
 * for a photographed bill, an <object> for a PDF. openBlob() (window.open of the same URL) is kept
 * as a second, explicitly labelled button for anybody who wants the file itself, because that one
 * does land in Downloads on most browsers and must not be the only way to look at the paper.
 *
 * ⛔ The figures are the OCR's AS STORED, in the document's own decimals, and nothing is added up:
 * taxOcr writes total = amount + amount0 + vat, so re-deriving it on screen could print a total the
 * portal's own Amount column disagrees with. A scan the OCR could not read says so (ocr_error).
 */
function ScanDocDialog({ clientId, id, docNo, onClose, record }: {
  clientId: string; id: string; docNo: string; onClose: () => void;
  /** The register row this document is shown under, when it has one — what "Open in Expenses"
   *  needs. A bill made from a scan and that scan share ONE row, so the key may not be this id's. */
  record?: { key: string; expenseNo: string };
}) {
  const { t } = useTranslation();
  const goToRecord = useGoToExpenseRecord();
  const [d, setD] = useState<AwExpenseDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setD(null); setErr('');
    stillStands(clientId, 'expense_upload', id)
      .then(there => {
        if (!alive) return null;
        if (!there) { setErr(t('aw_doc_menu_gone')); return null; }
        return api.awExpense(clientId, id).then((x: AwExpenseDetail) => { if (alive) setD(x); });
      })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [clientId, id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog fullscreen title={t('aw_sd_scan_title')} docNo={docNo || String(d?.filename || '')} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div style={errBox}>{err}</div>}
        {!d && !err && <Loader />}
        {d && (
          <>
            <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              <Fact label={t('aw_exp_vendor')}>{d.vendor || '—'}{d.vendor_tax_id ? <span style={{ color: 'var(--text-tertiary)' }}> · {d.vendor_tax_id}</span> : ''}</Fact>
              <Fact label={t('aw_col_no')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{d.doc_no || '—'}</span></Fact>
              <Fact label={t('aw_exp_doc_date')}>{d.doc_date ? dmy(d.doc_date) : '—'}</Fact>
              <Fact label={t('aw_col_status')}>
                {/* A scan the firm cancelled keeps its stored status (voided_at), so it says what the list and the panel say of it. */}
                <span style={statusPill(d.cancelled ? 'cancel' : d.status)}>
                  {d.cancelled ? t('aw_er_chip_cancelled') : SCAN_STATUS_KEY[d.status] ? t(SCAN_STATUS_KEY[d.status]) : (d.status || '—')}
                </span>
              </Fact>
              <Fact label={t('aw_exp_amount')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(d.amount)}</span></Fact>
              <Fact label={t('aw_pay_vat')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(d.vat)}</span></Fact>
              <Fact label={t('aw_sd_scan_amount0')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(d.amount0)}</span></Fact>
              <Fact label={t('aw_col_total')}><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{amt(d.total)}</span></Fact>
              <Fact label={t('aw_exp_file')}>{d.filename || '—'}</Fact>
            </div>
            {!!d.reject_reason && <div style={errBox}>{d.reject_reason}</div>}
            {!!d.ocr_error && <div style={note}>{t('aw_sd_scan_ocr_error', { reason: d.ocr_error })}</div>}
            {!!d.note && <div style={{ ...card, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{d.note}</div>}
            <ScanSheetView clientId={clientId} id={id} mime={String(d.mime || '')} filename={d.filename} pageCount={d.page_count}
              tools={sheet => (
                /* ⛔ ITS OWN blob URL, fetched on the click — never the one the <img> is using.
                   ui.ts openBlob owns what it is handed and revokes it after a minute, while the
                   sheet view revokes the inline URL the moment the sheet changes or the window closes:
                   sharing one would pull the file out from under whichever of the two outlived it.
                   DlBtn because it spins — this repo's rule for every button that fetches a file. */
                <DlBtn style={{ ...btn, padding: '4px 9px', fontSize: 12 }} title={t('aw_sd_scan_open_file')}
                  onRun={async () => openBlob(await api.awExpenseFileBlobUrl(clientId, id, sheet > 1 ? sheet : undefined))}>
                  <i className="ti ti-external-link" aria-hidden /> {t('aw_sd_scan_open_file')}
                </DlBtn>
              )} />
            <RecordEvidence clientId={clientId} kind="expense_upload" id={id} />
            <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <i className="ti ti-lock" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
              <div style={note}>{t('aw_sd_scan_readonly')}</div>
            </div>
            {/* ⛔ A LINK, not a second door. This window stays read-only; the register's own screen
                is the one carrying the gates, so the way to act on this document is to go there. */}
            {!!record?.key && (
              <button onClick={() => goToRecord(record.key, onClose)} style={openInExp}>
                <i className="ti ti-external-link" aria-hidden /> {t('aw_sd_open_in_expenses', { no: record.expenseNo || docNo })}
              </button>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * The source document, whichever kind it is — the one dispatcher.
 *
 * ⛔ The invoice families and receipts open DocDetail, the window the Sales list opens, so the
 * document reads identically whether it was reached from the list or from the ledger. `entries` is
 * false from a voucher: the reader arrived FROM it, and "what did this document post" would walk
 * them back to it one window deeper. (Owner 2026-11-02) A document's related documents open this
 * dispatcher too (relatedDocs.tsx), whose reader came from no voucher, so it says `entries`.
 */
/**
 * Go to the Expenses register, opened on this record.
 *
 * ⛔ The shell owns the URL (?menu=&sub=&exp=), so this writes the search params rather than
 * reaching into another pane — and it writes them through the ROUTER. The first cut called
 * history.pushState and dispatched a synthetic popstate, which worked and broke the house rule that
 * only utils/unsaved.ts steps the browser's history (testUnsavedGuard §… "the only code that steps
 * the browser's history is the registry"): the registry keeps a sentinel in history.state to catch
 * Back with unsaved work, and a push it did not make leaves that sentinel describing the wrong
 * entry.
 *
 * ⛔ THROUGH guardLeave, as ctx.setNav is. This is the one way out of a voucher's document that
 * LEAVES rather than stacks: it unmounts the pane underneath, and whatever window is open on it —
 * typing and all. Writing ?menu= straight past the guard moved the shell without asking whenever a
 * window below held unsaved work (testSourceDocJump §5h-bis). `close` runs only once the operator
 * has said yes, so choosing to keep editing leaves every window exactly where it was; one URL
 * write, replacing, as the shell's own menu moves do.
 */
export function useGoToExpenseRecord(): (key: string, close: () => void) => void {
  const [, setParams] = useSearchParams();
  return (key: string, close: () => void) => {
    guardLeave(() => {
      close();
      setParams(prev => {
        const n = new URLSearchParams(prev);
        n.set('menu', 'expenses'); n.set('sub', 'expense_records'); n.set('exp', key);
        return n;
      }, { replace: true });
    });
  };
}

const openInExp: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
  padding: '6px 11px', borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
};

/**
 * Go to Payroll › Payroll List, opened on this run (batch 10 R7, PAY-18's payroll branch; R7-26).
 *
 * ⛔ A PLACE, NOT A WINDOW. A payroll run has no read-only window to stack over the voucher the way an
 * invoice does: its screen is the run window in Payroll › Payroll List, which carries Post, Reverse and
 * Copy — the payroll entry's ONLY way back out of the books (journalPost refuses everything else for a
 * payroll-family entry). So this LEAVES, like "Open in Expenses" above: one URL write through the router,
 * replacing, inside guardLeave — a window below with unsaved typing is asked about once and nothing moves
 * on "keep editing". Nothing here closes the voucher: the move unmounts the pane it was opened from, and
 * the voucher with it.
 *
 * ⛔ `emp` and `etab` are dropped (the employee window's own keys), so a link the reader arrived on cannot
 * open an employee over the run. ?run= is the run window's own deep link (panes/PayrollRuns.tsx). Since R8
 * `prview` and `remit` go too (the Payroll List's Remittances view and its open remittance, R8-20): the run
 * window opens over the runs, never over a remittance left in the URL.
 */
function useGoToPayrollRun(): (runId: string) => void {
  const [, setParams] = useSearchParams();
  return (runId: string) => {
    guardLeave(() => {
      setParams(prev => {
        const n = new URLSearchParams(prev);
        n.set('menu', 'payroll'); n.set('sub', 'payroll_list'); n.set('run', runId);
        n.delete('emp'); n.delete('etab'); n.delete('prview'); n.delete('remit');
        return n;
      }, { replace: true });
    });
  };
}

/**
 * The voucher's way back to the payroll run that posted it — three answers, each a sentence or a button:
 *   · a read-only seat (a client's books login, the auditor) does not open Payroll › Payroll List at all,
 *     so it is told where the run lives and offered nothing that would land on "needs permission";
 *   · when the URL already holds this run, the run window is open BEHIND this voucher (reached from its own
 *     Entries): moving there would remount nothing and leave every window stacked, so it says so — Esc is
 *     the way back;
 *   · otherwise the button.
 * Its own component, so that only a payroll voucher reads the router: the voucher window is also rendered
 * where no router is mounted, and every other kind must keep working there.
 */
function PayrollRunOpen({ s }: { s: SourceRef }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [params] = useSearchParams();
  const goToRun = useGoToPayrollRun();
  const here = params.get('menu') === 'payroll' && params.get('sub') === 'payroll_list' && params.get('run') === s.id;
  if (readOnly || here) {
    return (
      <span style={{ ...note, color: 'var(--text-secondary)' }} data-payroll-open={readOnly ? 'seat' : 'here'}>
        <i className="ti ti-info-circle" style={{ marginRight: 5 }} aria-hidden />
        {readOnly ? t('aw_sd_payroll_seat', { docNo: s.docNo }) : t('aw_sd_payroll_here', { docNo: s.docNo })}
      </span>
    );
  }
  return (
    <button type="button" onClick={() => goToRun(s.id)} data-payroll-open="go"
      style={{ ...btn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
      <i className="ti ti-users" aria-hidden /> {t('aw_sd_open_payroll')}
    </button>
  );
}

/**
 * Go to Payroll › Payroll List › Remittances, opened on this remittance (batch 10 R8, R8-18 / R8-20).
 *
 * ⛔ A PLACE, NOT A WINDOW, for the reason a run is one (useGoToPayrollRun above): a PND.1 or SSO remittance is
 * reversed only from its own window in Payroll, the one door journalPost leaves open for its entries. So this
 * LEAVES the same way — one replacing URL write through the router, inside guardLeave, asked once when a window
 * below holds typing. ?prview=remittances is the Payroll List's own view switch (never the shell's ?view=) and
 * ?remit= opens the remittance; `run`, `emp` and `etab` are dropped, so neither a run window nor an employee
 * window can open over it from a link the reader arrived on.
 */
function useGoToPayrollRemittance(): (remitId: string) => void {
  const [, setParams] = useSearchParams();
  return (remitId: string) => {
    guardLeave(() => {
      setParams(prev => {
        const n = new URLSearchParams(prev);
        n.set('menu', 'payroll'); n.set('sub', 'payroll_list'); n.set('prview', 'remittances'); n.set('remit', remitId);
        n.delete('run'); n.delete('emp'); n.delete('etab');
        return n;
      }, { replace: true });
    });
  };
}

/**
 * The voucher's way to the PND.1 or SSO remittance that posted it — R7's three answers (PayrollRunOpen), each
 * a sentence or a button: a read-only seat is told where remittances are kept; with the remittance already open
 * behind this voucher (reached from its own Entries) it says so, Esc being the way back; otherwise the button.
 * `s.docNo` is the resolver's own name for it ("PND.1 10/2026"), never composed here.
 */
function PayrollRemitOpen({ s }: { s: SourceRef }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [params] = useSearchParams();
  const goToRemit = useGoToPayrollRemittance();
  const here = params.get('menu') === 'payroll' && params.get('sub') === 'payroll_list'
    && params.get('prview') === 'remittances' && params.get('remit') === s.id;
  if (readOnly || here) {
    return (
      <span style={{ ...note, color: 'var(--text-secondary)' }} data-remit-open={readOnly ? 'seat' : 'here'}>
        <i className="ti ti-info-circle" style={{ marginRight: 5 }} aria-hidden />
        {readOnly ? t('aw_sd_remit_seat', { docNo: s.docNo }) : t('aw_sd_remit_here', { docNo: s.docNo })}
      </span>
    );
  }
  return (
    <button type="button" onClick={() => goToRemit(s.id)} data-remit-open="go"
      style={{ ...btn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
      <i className="ti ti-receipt-tax" aria-hidden /> {t('aw_sd_open_remittance')}
    </button>
  );
}

/**
 * A list's number for a payroll run or a remittance, as the way to its place (owner 2026-11-02, "Trial Balance", section 3: source numbers are clickable). It leaves,
 * through the same two moves as the voucher's buttons above, and for the same seats: a read-only seat, or the place already open behind this page, gets the number
 * with the sentence on hover. Without a router (a screen mounted on its own) it is the number alone.
 */
function PayrollPlaceLink({ s, label }: { s: SourceRef; label: React.ReactNode }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [params] = useSearchParams();
  const goToRun = useGoToPayrollRun();
  const goToRemit = useGoToPayrollRemittance();
  const run = s.kind === 'payroll_run';
  const here = params.get('menu') === 'payroll' && params.get('sub') === 'payroll_list'
    && (run ? params.get('run') === s.id : params.get('prview') === 'remittances' && params.get('remit') === s.id);
  if (readOnly || here) {
    const key = run ? (readOnly ? 'aw_sd_payroll_seat' : 'aw_sd_payroll_here') : (readOnly ? 'aw_sd_remit_seat' : 'aw_sd_remit_here');
    return <span style={{ fontVariantNumeric: 'tabular-nums' }} title={t(key, { docNo: s.docNo })} data-src-text={s.kind}>{label}</span>;
  }
  return (
    <button type="button" onClick={() => (run ? goToRun(s.id) : goToRemit(s.id))} title={t(run ? 'aw_sd_open_payroll' : 'aw_sd_open_remittance')} data-src-place={s.kind}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
      {label}
    </button>
  );
}
function PayrollPlaceNo({ s, label }: { s: SourceRef; label: React.ReactNode }) {
  return useInRouterContext() ? <PayrollPlaceLink s={s} label={label} /> : <span style={{ fontVariantNumeric: 'tabular-nums' }} data-src-text={s.kind}>{label}</span>;
}

export function SourceDocDialog({ clientId, s, onClose, entries = false }: { clientId: string; s: SourceRef; onClose: () => void;
  /** Whether the document window offers its entries panel: off from a voucher (above), on where the reader did not come from one. */
  entries?: boolean;
}) {
  const { t } = useTranslation();
  const [busyPdf, setBusyPdf] = useState(false);
  const kind = String(s.kind || '');
  const family = DOC_FAMILY[kind];
  // ⛔ ALERTED, NOT PUT IN AN ERROR BOX, and that is the opposite of this file's usual habit for a
  // reason: the only surface this component owns is BEHIND the full-screen document window, so a
  // box here would be read minutes later, by somebody who had already closed the window and
  // concluded the PDF simply does not work. DlBtn does the same for the same reason.
  const openPdf = async () => {
    if (busyPdf || !family) return;
    setBusyPdf(true);
    try {
      // (Owner 2026-11-02) Asked first, as the list's Open PDF asks (Documents.tsx stillThere): a document's related documents open this
      // window too, it may stand open for minutes, and a row gone by then answers /pdf a 404 with a JSON body, which pages the admins.
      const v: AwDocActions = await api.awDocActions(clientId, family, s.id);
      if (!v.found) { window.alert(t('aw_doc_menu_gone')); return; }
      openBlob(await api.awDocPdfBlobUrl(clientId, family, s.id));
    }
    catch (e: any) { window.alert(String(e?.message || e) || t('aw_viewer_failed')); }
    finally { setBusyPdf(false); }
  };
  if (family) {
    return (
      /* The document's number and stored type come from the SERVER's resolver, so the window is
         headed correctly before its own read lands; status and the paired number arrive with it. */
      <DocDetail ctx={{ clientId }} kind={family} entries={entries} voided={!!s.voided} busyPdf={busyPdf} onPdf={openPdf} onClose={onClose}
        doc={{ id: s.id, doc_no: s.docNo, doc_type: kind }} />
    );
  }
  if (kind === 'payable') return <PayableDocDialog clientId={clientId} id={s.id} docNo={s.docNo} onClose={onClose} record={s.record} />;
  if (kind === 'expense_upload') return <ScanDocDialog clientId={clientId} id={s.id} docNo={s.docNo} onClose={onClose} record={s.record} />;
  if (kind === 'purchase_note') return <NoteDocDialog clientId={clientId} id={s.id} docNo={s.docNo} onClose={onClose} />;
  // Unreachable from SourceDocOpen below, which offers no control for a kind with no screen. Kept
  // so that adding a kind to the resolver cannot make a window open empty.
  return null;
}

/** The kinds the SERVER calls openable that this product has no screen for — one sentence each,
 *  naming the kind. ⛔ Not a single general excuse: "a statement line has no document screen, the
 *  line is in Banking › Transactions" is useful, "cannot be opened" is not, and the next kind added
 *  here has to earn its own sentence rather than inherit a vague one. (What KIND of document it is
 *  is said separately, by the voucher screen's own SRC_KEY labels.) */
const NO_SCREEN_KEY: Record<string, string> = {
  // ⛔ The one kind the server calls openable and this product cannot show. Named, not generalised.
  bank_line: 'aw_sd_no_screen_bank_line',
  // Batch 11 R8a (W2-30): a kind the entries panel names as a `via` — a recorded payment. It has no window of its own:
  // it is a section of the bill it belongs to, and the sentence says where that is rather than opening a window that
  // would have to invent one. (A supplier's booked note was the second such kind; since batch 12 tail T1 it opens
  // NoteDocDialog — screenOf's 'note'.)
  expense_payment: 'aw_sd_no_screen_expense_payment',
};

/**
 * The control the voucher window shows. ⛔ Three answers, never a dead link (see the header).
 *
 * The server's `why` rides as the hover text on the number in every case, so its exact wording is
 * never thrown away even where a sentence of our own is shown beside it.
 */
export function SourceDocOpen({ clientId, s, label, places }: {
  clientId: string; s: SourceRef | null | undefined;
  /** Batch 11 R8a: the document's number as the control itself (the entries panel's "via RE-…"), instead of the
   *  voucher window's "Open document" button. Where the kind has no window, the number is printed as text with the
   *  sentence on hover — the same three answers, compactly. */
  label?: React.ReactNode;
  /** With `label`, on a list: a payroll run's or remittance's number goes to its place. A voucher's own panels leave this off: the voucher offers that button itself. */
  places?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const kind = String(s?.kind || '');
  const why = String(s?.why || '');
  const docNo = String(s?.docNo || '').trim();
  // Nothing recorded at all: the voucher IS the record, and the head card already says so.
  if (!kind && !docNo) return <span style={note}>{why || t('aw_sd_none')}</span>;
  const screen = screenOf(kind);
  if (label !== undefined) {
    // A payroll run or remittance is a PLACE (PayrollRunOpen / PayrollRemitOpen), reached from the voucher window; as a
    // label it is the number alone, which names it.
    const sentence = !s?.openable ? (why || t('aw_sd_not_openable'))
      : !screen ? t(NO_SCREEN_KEY[kind] || 'aw_sd_no_screen') : '';
    if (places && s && !sentence && (screen === 'payroll' || screen === 'payroll_remit')) return <PayrollPlaceNo s={s} label={label} />;
    if (sentence || !opensAWindow(kind)) {
      return <span style={{ fontVariantNumeric: 'tabular-nums' }} title={sentence || undefined} data-src-text={kind}>{label}</span>;
    }
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} title={t('aw_sd_open_title')} data-src-link={kind}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
          {label}
        </button>
        {/* ⛔ PORTALLED TO THE BODY (review 2026-11-02), RecordWindow's own reason in relatedDocs.tsx: this one is held by a table cell, and a Dialog is not
            portalled itself, so the document window inherited the cell's nowrap and the number's weight, and ran off a phone's glass. */}
        {open && s && createPortal(<SourceDocDialog clientId={clientId} s={s} onClose={() => setOpen(false)} />, document.body)}
      </>
    );
  }
  if (!s?.openable) {
    // ⛔ Answer 2: the server's own sentence, no control. A voided document, a withdrawn VAT line, a
    // line typed straight into the VAT ledger, a hand-written voucher — all different sentences.
    return (
      <span style={{ ...note, color: 'var(--text-secondary)' }}>
        <i className="ti ti-info-circle" style={{ marginRight: 5 }} aria-hidden />
        {why || t('aw_sd_not_openable')}
      </span>
    );
  }
  // A payroll run is a place to go, not a window to stack (batch 10 R7 — PayrollRunOpen above), and so is a
  // PND.1 or SSO remittance (batch 10 R8 — PayrollRemitOpen).
  if (screen === 'payroll' && s) return <PayrollRunOpen s={s} />;
  if (screen === 'payroll_remit' && s) return <PayrollRemitOpen s={s} />;
  if (!screen) {
    // ⛔ Answer 3: openable to the server, no screen in this product. Reported, never bolted on.
    return (
      <span style={{ ...note, color: 'var(--text-secondary)' }} title={why || undefined}>
        <i className="ti ti-info-circle" style={{ marginRight: 5 }} aria-hidden />
        {t(NO_SCREEN_KEY[kind] || 'aw_sd_no_screen')}
      </span>
    );
  }
  return (
    <>
      {/* ⛔ A BUTTON, never an <a href>: the workspace is one route with its state in the search
          params, and an anchor would reload the shell and lose the period, the filters and the
          window stack. It does not navigate either — the document opens OVER the voucher, so the
          way back is Esc and nothing behind it is unmounted. */}
      <button type="button" onClick={() => setOpen(true)} title={t('aw_sd_open_title')}
        style={{ ...btn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
        <i className="ti ti-file-search" aria-hidden /> {t('aw_sd_open')}
      </button>
      {open && s && <SourceDocDialog clientId={clientId} s={s} onClose={() => setOpen(false)} />}
    </>
  );
}
